/**
 * Tool Card Component
 * Displays individual tool information in a card format
 */

import React from 'react'
import { Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Tooltip
} from '@mui/material'
import {
  PlayArrow as RunIcon,
  Schedule as ScheduleIcon,
  Numbers as CountIcon,
  ArrowForward as OpenIcon
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import { getToolDomain } from '../toolMeta'
import FpVisibilityIcon from '../../../../components/inspector/shared/FpVisibilityIcon'

interface ToolCardProps {
  tool: {
    id: string
    name: string
    description: string
    last_run?: string  // Support backend field name
    lastRun?: string  // Support legacy field
    runCount?: number
    requires_params?: boolean  // Support backend field name
    requiresInput?: boolean  // Support legacy field
  }
  icon: React.ReactNode
  onExecute: () => void
  hasPage?: boolean
  toolUrl?: string
}

const ToolCard: React.FC<ToolCardProps> = ({ tool, icon, onExecute, hasPage = false, toolUrl }) => {
  const isExport = tool.id === 'invoice_payments_export'
  const domain = getToolDomain(tool.id)

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: 2,
              backgroundColor: domain?.bg ?? 'rgba(0,127,152,0.10)',
              color: domain?.color ?? '#007f98',
              '& svg': { fontSize: 24 }
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="h3">
            {tool.name}
            <FpVisibilityIcon kind="tool" id={tool.id} />
          </Typography>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, minHeight: 48 }}
        >
          {tool.description}
        </Typography>

        {(domain || tool.requiresInput || tool.requires_params) && (
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            {domain && (
              <Chip
                label={domain.label}
                size="small"
                sx={{
                  backgroundColor: domain.bg,
                  color: domain.color,
                  fontWeight: 500,
                  border: 'none',
                }}
              />
            )}
            {(tool.requiresInput || tool.requires_params) && (
              <Chip
                label="Requires Input"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        )}

        {/* Stats */}
        <Box display="flex" gap={2} alignItems="center">
          {tool.runCount !== undefined && (
            <Tooltip title="Total runs">
              <Box display="flex" alignItems="center" gap={0.5}>
                <CountIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {tool.runCount} runs
                </Typography>
              </Box>
            </Tooltip>
          )}
          
          {(tool.lastRun || tool.last_run) && (
            <Tooltip title={`Last run: ${new Date(tool.lastRun || tool.last_run || '').toLocaleString()}`}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <ScheduleIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(new Date(tool.lastRun || tool.last_run || ''), { addSuffix: true })}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        {hasPage && toolUrl ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<OpenIcon />}
            fullWidth
            component={Link}
            to={toolUrl}
          >
            Open Tool
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            startIcon={<RunIcon />}
            fullWidth
            onClick={onExecute}
          >
            {isExport ? 'Run Export' : 'Open Tool'}
          </Button>
        )}
      </CardActions>
    </Card>
  )
}

export default ToolCard